// src/context/DataContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  get,
  set,
  onValue,
  push,
  update,
  remove,
} from "firebase/database";
import { uploadFile } from "../config/uploadFile";
import { app, storage } from "../config/firebaseConfig"; // Import storage here!

const db = getDatabase(app);
const DataContext = createContext();

export const DataProvider = ({ children }) => {
  // ---------------- ACTIVE PROPERTY (Home tabs scoping) ----------------
  // Default property is "vivaak" (Dharampur) — its data lives at root paths.
  // Other properties (e.g. "barog") live under `properties/{id}/...`.
  const [activeHomeProperty, setActiveHomePropertyState] = useState(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("adminActiveProperty") || "vivaak";
    }
    return "vivaak";
  });

  const setActiveHomeProperty = (id) => {
    setActiveHomePropertyState(id);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("adminActiveProperty", id);
    }
  };

  // Scope a Firebase path by the active property. Only `home/*` and
  // `gallery/photos/*` are property-scoped; everything else is shared.
  const homePath = (subpath) => {
    const scopable =
      subpath.startsWith("home/") || subpath.startsWith("gallery/photos");
    if (!scopable || activeHomeProperty === "vivaak" || !activeHomeProperty) {
      return subpath;
    }
    return `properties/${activeHomeProperty}/${subpath}`;
  };

  // List of property-scoped subtrees we copy/seed wholesale.
  const SCOPED_SUBTREES = [
    "home/homeHero",
    "home/about",
    "home/vivaakOffering",
    "home/amenitiesServices",
    "home/perfectForCarousel",
    "home/villaInMotionGallery",
    "home/localAttractions",
    "home/specialOffers",
    "home/testimonials",
    "home/faqs",
    "home/weMakeItSpecial",
    "gallery/photos",
  ];

  // Copy every property-scoped subtree from Vivaak (root) into the given
  // target property. Image URLs are shared (we don't re-upload files), so
  // the target gets the same images until the admin replaces them.
  const cloneVivaakDataTo = async (targetPropertyId) => {
    if (!targetPropertyId || targetPropertyId === "vivaak") {
      throw new Error("Target property must be set and cannot be 'vivaak'.");
    }
    for (const sub of SCOPED_SUBTREES) {
      const snap = await get(ref(db, sub));
      const data = snap.val();
      if (data == null) continue;
      await set(ref(db, `properties/${targetPropertyId}/${sub}`), data);
    }
  };

  // ---------------- HEADER 1 ----------------
  const [headerLinks, setHeaderLinks] = useState([]);
  const [headerConfig, setHeaderConfig] = useState({
    logoUrl: "",
    darkLogoUrl: "",
    menuIconUrl: "",
    closeIconUrl: "",
    stickyBgColor: "",
    stickyTextColor: "",
  });

  useEffect(() => {
    const header1Ref = ref(db, "header/header1");
    return onValue(header1Ref, (snap) => {
      const data = snap.val() || {};
      setHeaderConfig({
        logoUrl: data.logoUrl || "",
        darkLogoUrl: data.darkLogoUrl || "",
        menuIconUrl: data.menuIconUrl || "",
        closeIconUrl: data.closeIconUrl || "",
        stickyBgColor: data.stickyBgColor || "",
        stickyTextColor: data.stickyTextColor || "",
      });
      const linksObj = data.links || {};
      const linksArray = Object.entries(linksObj).map(([id, val]) => ({
        id,
        ...val,
      }));
      setHeaderLinks(linksArray);
    });
  }, []);

  const addHeaderLink = async (linkData) =>
    await push(ref(db, "header/header1/links"), linkData);
  const updateHeaderLink = async (id, updates) =>
    await update(ref(db, `header/header1/links/${id}`), updates);
  const deleteHeaderLink = async (id) =>
    await remove(ref(db, `header/header1/links/${id}`));
  const updateHeaderConfig = async (updates) =>
    await update(ref(db, "header/header1"), updates);
  const uploadHeaderAsset = async (file, fieldName) => {
    const url = await uploadFile(file);
    await updateHeaderConfig({ [fieldName]: url });
  };

  // ---------------- HEADER 2 ----------------
  const [header2Links, setHeader2Links] = useState([]);
  const [header2Config, setHeader2Config] = useState({
    logoUrl: "",
    menuIconUrl: "",
    closeIconUrl: "",
  });

  useEffect(() => {
    const header2Ref = ref(db, "header/header2");
    return onValue(header2Ref, (snap) => {
      const data = snap.val() || {};
      setHeader2Config({
        logoUrl: data.logoUrl || "",
        menuIconUrl: data.menuIconUrl || "",
        closeIconUrl: data.closeIconUrl || "",
      });
      const linksObj = data.links || {};
      const linksArray = Object.entries(linksObj).map(([id, val]) => ({
        id,
        ...val,
      }));
      setHeader2Links(linksArray);
    });
  }, []);

  const addHeader2Link = async (linkData) =>
    await push(ref(db, "header/header2/links"), linkData);
  const updateHeader2Link = async (id, updates) =>
    await update(ref(db, `header/header2/links/${id}`), updates);
  const deleteHeader2Link = async (id) =>
    await remove(ref(db, `header/header2/links/${id}`));
  const updateHeader2Config = async (updates) =>
    await update(ref(db, "header/header2"), updates);
  const uploadHeader2Asset = async (file, fieldName) => {
    const url = await uploadFile(file);
    await updateHeader2Config({ [fieldName]: url });
  };

  // ---------------- HOME - HERO ----------------
  const [heroConfig, setHeroConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [], // <-- now an array
  });

  useEffect(() => {
    const heroRef = ref(db, homePath("home/homeHero"));
    return onValue(heroRef, async (snap) => {
      const data = snap.val() || {};
      // Auto-seed "main" homepage from dharampur (vivaak root) if it has no data yet
      if (activeHomeProperty === "main" && !data.backgroundUrls?.length) {
        const rootSnap = await get(ref(db, "home/homeHero"));
        const rootData = rootSnap.val() || {};
        if (rootData.backgroundUrls?.length) {
          await set(ref(db, "properties/main/home/homeHero"), rootData);
          setHeroConfig({
            heroText: rootData.heroText || "",
            highlightText: rootData.highlightText || "",
            backgroundUrls: rootData.backgroundUrls || [],
          });
          return;
        }
      }
      setHeroConfig({
        heroText: data.heroText || "",
        highlightText: data.highlightText || "",
        backgroundUrls: data.backgroundUrls || [],
      });
    });
  }, [activeHomeProperty]);

  const updateHeroConfig = async (updates) =>
    await update(ref(db, homePath("home/homeHero")), updates);

  // Accepts an array of files and uploads all, then updates backgroundUrls array
  const uploadHeroBackground = async (files) => {
    const urls = [];
    for (const file of files) {
      const url = await uploadFile(file); // Upload to Firebase Storage
      urls.push(url);
    }

    const heroRef = ref(db, homePath("home/homeHero"));
    const snapshot = await get(heroRef); // fetch current data
    const existingData = snapshot.val() || {};
    const existingUrls = existingData.backgroundUrls || [];

    // Append new URLs to existing ones
    const updatedUrls = [...existingUrls, ...urls];

    // Update only backgroundUrls in the database
    await updateHeroConfig({ backgroundUrls: updatedUrls });

    return urls; // so component can also update local state if needed
  };

  const deleteHeroBackgroundImage = async (indexToRemove) => {
    const heroRef = ref(db, homePath("home/homeHero"));
    const snapshot = await get(heroRef);
    const data = snapshot.val() || {};
    const urls = data.backgroundUrls || [];
    const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);
    await updateHeroConfig({ backgroundUrls: updatedUrls });
  };

  // ---------------- HOME - ABOUT ----------------
  const [aboutConfig, setAboutConfig] = useState({
    heading: "",
    description: "",
    buttonText: "",
    buttonLink: "",
    imageUrl: "",
  });

  useEffect(() => {
    const aboutRef = ref(db, homePath("home/about"));
    return onValue(aboutRef, (snap) => {
      const data = snap.val() || {};
      setAboutConfig({
        heading: data.heading || "",
        description: data.description || "",
        buttonText: data.buttonText || "",
        buttonLink: data.buttonLink || "",
        imageUrl: data.imageUrl || "",
      });
    });
  }, [activeHomeProperty]);

  const updateAboutConfig = async (updates) =>
    await update(ref(db, homePath("home/about")), updates);
  const uploadAboutImage = async (file) => {
    const url = await uploadFile(file);
    await updateAboutConfig({ imageUrl: url });
  };

  // ---------------- HOME - VIVAAK OFFERINGS ----------------
  const [vivaakOfferings, setVivaakOfferings] = useState([]);

  useEffect(() => {
    const vivaakRef = ref(db, homePath("home/vivaakOffering"));
    return onValue(vivaakRef, (snap) => {
      const data = snap.val();
      setVivaakOfferings(data?.offerings || []);
    });
  }, [activeHomeProperty]);

  const postVivaakOfferings = async (offerings) => {
    const vivaakRef = ref(db, homePath("home/vivaakOffering"));
    await set(vivaakRef, { offerings });
  };

  // ---------------- HOME - AMENITIES SERVICES ----------------
  const [amenitiesServices, setAmenitiesServices] = useState([]);

  useEffect(() => {
    const amenitiesRef = ref(db, homePath("home/amenitiesServices"));
    return onValue(amenitiesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAmenitiesServices(data);
      } else {
        setAmenitiesServices([]);
      }
    });
  }, [activeHomeProperty]);

  // ---------------- HOME - PERFECT FOR CAROUSEL ----------------
  const [carouselData, setCarouselData] = useState([]);

  useEffect(() => {
    const carouselRef = ref(db, homePath("home/perfectForCarousel"));
    return onValue(carouselRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setCarouselData(formattedData);
      } else {
        setCarouselData([]);
      }
    });
  }, [activeHomeProperty]);

  const addCarouselCard = async (cardData, file) => {
    const carouselRef = ref(db, homePath("home/perfectForCarousel"));

    if (file) {
      // Upload the file and get the URL
      const uploadedUrl = await uploadFile(file);
      cardData.image = uploadedUrl; // Add the uploaded URL to the card data
    }

    await push(carouselRef, cardData); // Push the card data to Firebase
  };

  const updateCarouselCard = async (id, updates, file) => {
    const cardRef = ref(db, homePath(`home/perfectForCarousel/${id}`));

    if (file) {
      // Upload the file and get the URL
      const uploadedUrl = await uploadFile(file);
      updates.image = uploadedUrl; // Add the uploaded URL to the updates
    }

    await update(cardRef, updates); // Update the card data in Firebase
  };

  const deleteCarouselCard = async (id) => {
    const cardRef = ref(db, homePath(`home/perfectForCarousel/${id}`));
    await remove(cardRef);
  };

  // ---------------- VILLA IN MOTION GALLERY ----------------
  const [villaVideos, setVillaVideos] = useState([]);

  useEffect(() => {
    const villaRef = ref(db, homePath("home/villaInMotionGallery"));
    return onValue(villaRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setVillaVideos(formattedData);
      } else {
        setVillaVideos([]);
      }
    });
  }, [activeHomeProperty]);

  const addVillaVideo = async (videoData, file) => {
    const villaRef = ref(db, homePath("home/villaInMotionGallery"));

    if (file) {
      // Upload the video file and get the URL
      const uploadedUrl = await uploadFile(file);
      videoData.src = uploadedUrl; // Add the uploaded URL to the video data
    }

    await push(villaRef, videoData); // Push the video data to Firebase
  };

  const updateVillaVideo = async (id, updates, file) => {
    const videoRef = ref(db, homePath(`home/villaInMotionGallery/${id}`));

    if (file) {
      // Upload the video file and get the URL
      const uploadedUrl = await uploadFile(file);
      updates.src = uploadedUrl; // Add the uploaded URL to the updates
    }

    await update(videoRef, updates); // Update the video data in Firebase
  };

  const deleteVillaVideo = async (id) => {
    const videoRef = ref(db, homePath(`home/villaInMotionGallery/${id}`));
    await remove(videoRef);
  };

  // ---------------- LOCAL ATTRACTIONS ----------------
  const [localAttractions, setLocalAttractions] = useState([]);

  useEffect(() => {
    const attractionsRef = ref(db, homePath("home/localAttractions"));
    return onValue(attractionsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setLocalAttractions(formattedData);
      } else {
        setLocalAttractions([]);
      }
    });
  }, [activeHomeProperty]);

  const addLocalAttraction = async (attractionData, file) => {
    const attractionsRef = ref(db, homePath("home/localAttractions"));

    if (file) {
      // Upload the image file and get the URL
      const uploadedUrl = await uploadFile(file);
      attractionData.image = uploadedUrl; // Add the uploaded URL to the attraction data
    }

    await push(attractionsRef, attractionData); // Push the attraction data to Firebase
  };

  const updateLocalAttraction = async (id, updates, file) => {
    const attractionRef = ref(db, homePath(`home/localAttractions/${id}`));

    if (file) {
      // Upload the image file and get the URL
      const uploadedUrl = await uploadFile(file);
      updates.image = uploadedUrl; // Add the uploaded URL to the updates
    }

    await update(attractionRef, updates); // Update the attraction data in Firebase
  };

  const deleteLocalAttraction = async (id) => {
    const attractionRef = ref(db, homePath(`home/localAttractions/${id}`));
    await remove(attractionRef);
  };

  // ...existing code...

  // ---------------- SPECIAL OFFERS ----------------
  const [specialOffers, setSpecialOffers] = useState([]);

  useEffect(() => {
    const offersRef = ref(db, homePath("home/specialOffers"));
    return onValue(offersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setSpecialOffers(formattedData);
      } else {
        setSpecialOffers([]);
      }
    });
  }, [activeHomeProperty]);

  const addSpecialOffer = async (offerData, file) => {
    const offersRef = ref(db, homePath("home/specialOffers"));

    if (file) {
      // Upload the image file and get the URL
      const uploadedUrl = await uploadFile(file);
      offerData.image = uploadedUrl; // Add the uploaded URL to the offer data
    }

    await push(offersRef, offerData); // Push the offer data to Firebase
  };

  const updateSpecialOffer = async (id, updates, file) => {
    const offerRef = ref(db, homePath(`home/specialOffers/${id}`));

    if (file) {
      // Upload the image file and get the URL
      const uploadedUrl = await uploadFile(file);
      updates.image = uploadedUrl; // Add the uploaded URL to the updates
    }

    await update(offerRef, updates); // Update the offer data in Firebase
  };

  const deleteSpecialOffer = async (id) => {
    const offerRef = ref(db, homePath(`home/specialOffers/${id}`));
    await remove(offerRef);
  };

  // ...existing code...

  // ---------------- TESTIMONIALS ----------------
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const testimonialsRef = ref(db, homePath("home/testimonials"));
    return onValue(testimonialsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setTestimonials(formattedData);
      } else {
        setTestimonials([]);
      }
    });
  }, [activeHomeProperty]);

  const addTestimonial = async (testimonialData, file) => {
    const testimonialsRef = ref(db, homePath("home/testimonials"));

    if (file) {
      // Upload the avatar image file and get the URL
      const uploadedUrl = await uploadFile(file);
      testimonialData.avatar = uploadedUrl; // Add the uploaded URL to the testimonial data
    }

    await push(testimonialsRef, testimonialData); // Push the testimonial data to Firebase
  };

  const updateTestimonial = async (id, updates, file) => {
    const testimonialRef = ref(db, homePath(`home/testimonials/${id}`));

    if (file) {
      // Upload the avatar image file and get the URL
      const uploadedUrl = await uploadFile(file);
      updates.avatar = uploadedUrl; // Add the uploaded URL to the updates
    }

    await update(testimonialRef, updates); // Update the testimonial data in Firebase
  };

  const deleteTestimonial = async (id) => {
    const testimonialRef = ref(db, homePath(`home/testimonials/${id}`));
    await remove(testimonialRef);
  };

  // ...existing code...

  // ---------------- FAQS ----------------
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const faqsRef = ref(db, homePath("home/faqs"));
    return onValue(faqsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setFaqs(formattedData);
      } else {
        setFaqs([]);
      }
    });
  }, [activeHomeProperty]);

  const addFaq = async (faqData) => {
    const faqsRef = ref(db, homePath("home/faqs"));
    await push(faqsRef, faqData); // Push the FAQ data to Firebase
  };

  const updateFaq = async (id, updates) => {
    const faqRef = ref(db, homePath(`home/faqs/${id}`));
    await update(faqRef, updates); // Update the FAQ data in Firebase
  };

  const deleteFaq = async (id) => {
    const faqRef = ref(db, homePath(`home/faqs/${id}`));
    await remove(faqRef); // Delete the FAQ data from Firebase
  };

  // ...existing code...

  // ---------------- WE MAKE IT SPECIAL ----------------
  const [weMakeItSpecialContent, setWeMakeItSpecialContent] = useState({
    upperText: "",
    heading: "",
    paragraphs: ["", ""],
    buttonText: "",
    leftImage: "",
    overlayImage: "",
  });

  useEffect(() => {
    const specialRef = ref(db, homePath("home/weMakeItSpecial"));
    return onValue(specialRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setWeMakeItSpecialContent({
          upperText: data.upperText || "",
          heading: data.heading || "",
          paragraphs: data.paragraphs || ["", ""],
          buttonText: data.buttonText || "",
          leftImage: data.leftImage || "",
          overlayImage: data.overlayImage || "",
        });
      }
    });
  }, [activeHomeProperty]);

  const updateWeMakeItSpecialContent = async (updates, files) => {
    const specialRef = ref(db, homePath("home/weMakeItSpecial"));

    if (files?.leftImage) {
      const leftImageUrl = await uploadFile(files.leftImage);
      updates.leftImage = leftImageUrl;
    }

    if (files?.overlayImage) {
      const overlayImageUrl = await uploadFile(files.overlayImage);
      updates.overlayImage = overlayImageUrl;
    }

    await update(specialRef, updates);
  };

  // ...existing code...

  // ---------------- BLOGS ----------------
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const blogsRef = ref(db, "blogs");
    return onValue(blogsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setBlogs(formattedData);
      } else {
        setBlogs([]);
      }
    });
  }, []);

  const addBlog = async (blogData, files) => {
    const blogsRef = ref(db, "blogs");

    // Upload images and get their URLs
    const uploadedImages = await Promise.all(
      files.map(async (file) => {
        const uploadedUrl = await uploadFile(file);
        return uploadedUrl;
      })
    );

    // Add uploaded image URLs to the blog data
    blogData.images = uploadedImages;

    await push(blogsRef, blogData); // Push the blog data to Firebase
  };

  const updateBlog = async (id, updates, files) => {
    const blogRef = ref(db, `blogs/${id}`);

    // Upload new images if provided
    if (files.length > 0) {
      const uploadedImages = await Promise.all(
        files.map(async (file) => {
          const uploadedUrl = await uploadFile(file);
          return uploadedUrl;
        })
      );
      updates.images = uploadedImages;
    }

    await update(blogRef, updates); // Update the blog data in Firebase
  };

  const deleteBlog = async (id) => {
    const blogRef = ref(db, `blogs/${id}`);
    await remove(blogRef); // Delete the blog data from Firebase
  };

  // ---------------- FLAVOURS - HERO ----------------
  const [flavoursHeroConfig, setFlavoursHeroConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [], // Now supports multiple images
  });

  // Fetch the config from Firebase
  useEffect(() => {
    const heroRef = ref(db, "flavours/flavoursHero");
    return onValue(heroRef, (snap) => {
      const data = snap.val() || {};
      setFlavoursHeroConfig({
        heroText: data.heroText || "",
        highlightText: data.highlightText || "",
        backgroundUrls: data.backgroundUrls || [],
      });
    });
  }, []);

  // Update method
  const updateFlavoursHeroConfig = async (updates) => {
    const heroRef = ref(db, "flavours/flavoursHero");
    await update(heroRef, updates);
  };

  // Upload multiple background images and append to the array
  const uploadFlavoursHeroBackground = async (files) => {
    const urls = [];

    for (const file of files) {
      const url = await uploadFile(file); // uploadFile is your helper that returns download URL
      urls.push(url);
    }

    const heroRef = ref(db, "flavours/flavoursHero");
    const snapshot = await get(heroRef);
    const existingData = snapshot.val() || {};
    const existingUrls = existingData.backgroundUrls || [];

    const updatedUrls = [...existingUrls, ...urls];

    await updateFlavoursHeroConfig({ backgroundUrls: updatedUrls });

    return urls;
  };

  // Delete a specific background image by index
  const deleteFlavoursHeroImage = async (indexToRemove) => {
    const heroRef = ref(db, "flavours/flavoursHero");
    const snapshot = await get(heroRef);
    const data = snapshot.val() || {};
    const urls = data.backgroundUrls || [];

    const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);

    await updateFlavoursHeroConfig({ backgroundUrls: updatedUrls });
  };

  // ---------------- OUR CHEFS ----------------
  const [ourChefs, setOurChefs] = useState([]);

  useEffect(() => {
    const chefsRef = ref(db, "flavours/ourChefs");
    return onValue(chefsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setOurChefs(formattedData);
      } else {
        setOurChefs([]);
      }
    });
  }, []);

  const addChef = async (chefData, imageFile) => {
    const chefsRef = ref(db, "flavours/ourChefs");
    const newChefRef = push(chefsRef); // Create a new entry

    let imageUrl = "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the URL
    }

    const newChefData = {
      ...chefData,
      imageUrl, // Add the uploaded image URL
    };

    await set(newChefRef, newChefData); // Save the chef data
  };

  const updateChef = async (id, updates, imageFile) => {
    const chefRef = ref(db, `flavours/ourChefs/${id}`);

    let imageUrl = updates.imageUrl; // Use the existing image URL by default
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedChefData = {
      ...updates,
      imageUrl, // Update the image URL
    };

    await update(chefRef, updatedChefData); // Update the chef data
  };

  const deleteChef = async (id) => {
    const chefRef = ref(db, `flavours/ourChefs/${id}`);
    await remove(chefRef); // Delete the chef data
  };

  // ---------------- MEAL TYPES ----------------
  const [mealTypes, setMealTypes] = useState([]);

  useEffect(() => {
    const mealTypesRef = ref(db, "flavours/mealTypes");
    return onValue(mealTypesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setMealTypes(formattedData);
      } else {
        setMealTypes([]);
      }
    });
  }, []);

  const addMealType = async (mealData, imageFile) => {
    const mealTypesRef = ref(db, "flavours/mealTypes");
    const newMealRef = push(mealTypesRef); // Create a new entry

    let imageUrl = "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the URL
    }

    const newMealData = {
      ...mealData,
      image: imageUrl, // Add the uploaded image URL
    };

    await set(newMealRef, newMealData); // Save the meal type data
  };

  const updateMealType = async (id, updates, imageFile) => {
    const mealRef = ref(db, `flavours/mealTypes/${id}`);

    let imageUrl = updates.image; // Use the existing image URL by default
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedMealData = {
      ...updates,
      image: imageUrl, // Update the image URL
    };

    await update(mealRef, updatedMealData); // Update the meal type data
  };

  const deleteMealType = async (id) => {
    const mealRef = ref(db, `flavours/mealTypes/${id}`);
    await remove(mealRef); // Delete the meal type data
  };

  // ---------------- EXPLORE YOUR MENU ----------------
  const [exploreMenuData, setExploreMenuData] = useState({});

  useEffect(() => {
    const exploreMenuRef = ref(db, "flavours/exploreMenu");
    return onValue(exploreMenuRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setExploreMenuData(data);
      } else {
        setExploreMenuData({});
      }
    });
  }, []);

  const updateExploreMenu = async (updates, imageFile) => {
    const exploreMenuRef = ref(db, "flavours/exploreMenu");

    let imageUrl = updates.image || ""; // Use the existing image URL by default
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedData = {
      ...updates,
      image: imageUrl, // Update the image URL
    };

    await set(exploreMenuRef, updatedData); // Update the "Explore Your Menu" data
  };

  const deleteExploreMenu = async () => {
    const exploreMenuRef = ref(db, "flavours/exploreMenu");
    await remove(exploreMenuRef); // Delete the "Explore Your Menu" data
  };

  // ---------------- HOSPITALITY TEAM ----------------
  const [hospitalityTeamData, setHospitalityTeamData] = useState({});

  useEffect(() => {
    const hospitalityRef = ref(db, "hospitality/hero");
    return onValue(hospitalityRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setHospitalityTeamData(data);
      } else {
        setHospitalityTeamData({});
      }
    });
  }, []);

  const updateHospitalityTeam = async (updates, imageFile) => {
    const hospitalityRef = ref(db, "hospitality/hero");

    let imageUrl = updates.image || ""; // Use the existing image URL by default
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedData = {
      ...updates,
      image: imageUrl, // Update the image URL
    };

    await set(hospitalityRef, updatedData); // Update the "Hospitality Team" data
  };

  const deleteHospitalityTeam = async () => {
    const hospitalityRef = ref(db, "hospitality/hero");
    await remove(hospitalityRef); // Delete the "Hospitality Team" data
  };

  // ---------------- TEAM PHOTOS ----------------
  const [teamPhotos, setTeamPhotos] = useState([]);

  useEffect(() => {
    const teamPhotosRef = ref(db, "hospitality/teamPhotos");
    return onValue(teamPhotosRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setTeamPhotos(formattedData);
      } else {
        setTeamPhotos([]);
      }
    });
  }, []);

  const addTeamPhoto = async (file) => {
    const teamPhotosRef = ref(db, "hospitality/teamPhotos");
    const newPhotoRef = push(teamPhotosRef); // Create a new entry

    let imageUrl = "";
    if (file) {
      imageUrl = await uploadFile(file); // Upload the file and get the URL
    }

    const newPhotoData = {
      imageUrl, // Add the uploaded image URL
    };

    await set(newPhotoRef, newPhotoData); // Save the photo data
  };

  const deleteTeamPhoto = async (id) => {
    const photoRef = ref(db, `hospitality/teamPhotos/${id}`);
    await remove(photoRef); // Delete the photo data
  };

  // ---------------- ABOUT - HERO ----------------
  const [aboutHeroConfig, setAboutHeroConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [],
  });

  useEffect(() => {
    const heroRef = ref(db, "about/aboutHero");
    return onValue(heroRef, (snap) => {
      const data = snap.val() || {};
      setAboutHeroConfig({
        heroText: data.heroText || "",
        highlightText: data.highlightText || "",
        backgroundUrls: data.backgroundUrls || [],
      });
    });
  }, []);

  // Method to post new About Hero data (array support)
  const postAboutHeroConfig = async (data, files) => {
    const heroRef = ref(db, "about/aboutHero");

    let backgroundUrls = [];
    if (files && files.length > 0) {
      backgroundUrls = await Promise.all(
        files.map(async (file) => await uploadFile(file))
      );
    }

    const newHeroData = {
      heroText: data.heroText || "",
      highlightText: data.highlightText || "",
      backgroundUrls,
    };

    await set(heroRef, newHeroData);
  };

  // Method to update existing About Hero data (array support)
  const updateAboutHeroConfig = async (updates, files) => {
    const heroRef = ref(db, "about/aboutHero");

    let backgroundUrls = updates.backgroundUrls || [];
    if (files && files.length > 0) {
      const uploaded = await Promise.all(
        files.map(async (file) => await uploadFile(file))
      );
      backgroundUrls = [...backgroundUrls, ...uploaded];
    }

    const updatedHeroData = {
      ...updates,
      backgroundUrls,
    };

    await update(heroRef, updatedHeroData);
  };

  // Delete a background image by index
  const deleteAboutHeroBackgroundImage = async (indexToRemove) => {
    const heroRef = ref(db, "about/aboutHero");
    const snapshot = await get(heroRef);
    const data = snapshot.val() || {};
    const urls = data.backgroundUrls || [];
    const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);
    await update(heroRef, { backgroundUrls: updatedUrls });
  };

  // ---------------- ABOUT INFO ----------------
  const [aboutInfoData, setAboutInfoData] = useState({
    heading: "",
    subheading: "",
    paragraphs: ["", ""],
    stats: {
      guests: "",
      memories: "",
      nights: "",
    },
  });

  useEffect(() => {
    const aboutInfoRef = ref(db, "about/aboutInfo");
    return onValue(aboutInfoRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAboutInfoData({
          heading: data.heading || "",
          subheading: data.subheading || "",
          paragraphs: data.paragraphs || ["", ""],
          stats: data.stats || { guests: "", memories: "", nights: "" },
        });
      }
    });
  }, []);

  const updateAboutInfo = async (updates) => {
    const aboutInfoRef = ref(db, "about/aboutInfo");
    await update(aboutInfoRef, updates); // Update the "About Info" data
  };

  const deleteAboutInfo = async () => {
    const aboutInfoRef = ref(db, "about/aboutInfo");
    await remove(aboutInfoRef); // Delete the "About Info" data
  };

  // ---------------- FAMILY LEGACY ----------------
  const [familyLegacyData, setFamilyLegacyData] = useState({
    timeline: ["1991", "2002", "2012", "Present"],
    image: "",
    heading: "",
    description: "",
  });

  useEffect(() => {
    const familyLegacyRef = ref(db, "about/familyLegacy");
    return onValue(familyLegacyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setFamilyLegacyData({
          timeline: data.timeline || ["1991", "2002", "2012", "Present"],
          image: data.image || "",
          heading: data.heading || "",
          description: data.description || "",
        });
      }
    });
  }, []);

  const updateFamilyLegacy = async (updates, imageFile) => {
    const familyLegacyRef = ref(db, "about/familyLegacy");

    let imageUrl = updates.image || "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedData = {
      ...updates,
      image: imageUrl, // Update the image URL
    };

    await update(familyLegacyRef, updatedData); // Update the "Family Legacy" data
  };

  const deleteFamilyLegacy = async () => {
    const familyLegacyRef = ref(db, "about/familyLegacy");
    await remove(familyLegacyRef); // Delete the "Family Legacy" data
  };

  // ---------------- FACILITIES ----------------
  const [facilitiesData, setFacilitiesData] = useState([]);

  useEffect(() => {
    const facilitiesRef = ref(db, "about/facilities");
    return onValue(facilitiesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setFacilitiesData(formattedData);
      } else {
        setFacilitiesData([]);
      }
    });
  }, []);

  const addFacility = async (facilityData, imageFile) => {
    const facilitiesRef = ref(db, "about/facilities");
    const newFacilityRef = push(facilitiesRef); // Create a new entry

    let imageUrl = "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the URL
    }

    const newFacility = {
      ...facilityData,
      image: imageUrl, // Add the uploaded image URL
    };

    await set(newFacilityRef, newFacility); // Save the facility data
  };

  const updateFacility = async (id, updates, imageFile) => {
    const facilityRef = ref(db, `about/facilities/${id}`);

    let imageUrl = updates.image || "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedFacility = {
      ...updates,
      image: imageUrl, // Update the image URL
    };

    await update(facilityRef, updatedFacility); // Update the facility data
  };

  const deleteFacility = async (id) => {
    const facilityRef = ref(db, `about/facilities/${id}`);
    await remove(facilityRef); // Delete the facility data
  };

  // ---------------- AMENITIES & SERVICES ----------------
  const [amenitiesData, setAmenitiesData] = useState([]);

  useEffect(() => {
    const amenitiesRef = ref(db, "about/amenitiesServices");
    return onValue(amenitiesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setAmenitiesData(formattedData);
      } else {
        setAmenitiesData([]);
      }
    });
  }, []);

  const addAmenity = async (amenityData, iconFile) => {
    const amenitiesRef = ref(db, "about/amenitiesServices");
    const newAmenityRef = push(amenitiesRef); // Create a new entry

    let iconUrl = "";
    if (iconFile) {
      iconUrl = await uploadFile(iconFile); // Upload the file and get the URL
    }

    const newAmenity = {
      ...amenityData,
      icon: iconUrl, // Add the uploaded icon URL
    };

    await set(newAmenityRef, newAmenity); // Save the amenity data
  };

  const updateAmenity = async (id, updates, iconFile) => {
    const amenityRef = ref(db, `about/amenitiesServices/${id}`);

    let iconUrl = updates.icon || "";
    if (iconFile) {
      iconUrl = await uploadFile(iconFile); // Upload the file and get the new URL
    }

    const updatedAmenity = {
      ...updates,
      icon: iconUrl, // Update the icon URL
    };

    await update(amenityRef, updatedAmenity); // Update the amenity data
  };

  const deleteAmenity = async (id) => {
    const amenityRef = ref(db, `about/amenitiesServices/${id}`);
    await remove(amenityRef); // Delete the amenity data
  };

  // ---------------- ABOUT VIDEO ----------------
  const [aboutVideo, setAboutVideo] = useState({
    videoUrl: "",
    heading: "",
    subheading: "",
  });

  useEffect(() => {
    const heroVideoRef = ref(db, "about/video");
    return onValue(heroVideoRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAboutVideo({
          videoUrl: data.videoUrl || "",
          heading: data.heading || "",
          subheading: data.subheading || "",
        });
      }
    });
  }, []);

  const updateAboutVideo = async (updates, videoFile) => {
    const heroVideoRef = ref(db, "about/video");

    let videoUrl = updates.videoUrl || "";
    if (videoFile) {
      videoUrl = await uploadFile(videoFile); // Upload the video file and get the URL
    }

    const updatedData = {
      ...updates,
      videoUrl, // Update the video URL
    };

    await update(heroVideoRef, updatedData); // Update the Hero Video data
  };

  // ---------------- GALLERY  ----------------

  // ---------------- GALLERY - Hero ----------------

  const [galleryHeroConfig, setGalleryHeroConfig] = useState({
    heroText: "",
    highlightText: "",
    backgroundUrls: [],
  });

  useEffect(() => {
    const heroRef = ref(db, "gallery/hero");
    return onValue(heroRef, (snap) => {
      const data = snap.val() || {};
      setGalleryHeroConfig({
        heroText: data.heroText || "",
        highlightText: data.highlightText || "",
        backgroundUrls: data.backgroundUrls || [],
      });
    });
  }, []);

  // Use this in your DataContext or utility file

  // Update hero config (partial update)
  const updateGalleryHeroConfig = async (updates) => {
    const heroRef = ref(db, "gallery/hero");
    await update(heroRef, updates);
  };

  // Upload multiple images and append to gallery
  const uploadGalleryHeroBackground = async (files) => {
    const heroRef = ref(db, "gallery/hero");
    const snapshot = await get(heroRef);
    const existingData = snapshot.val() || {};
    const existingUrls = existingData.backgroundUrls || [];
    const urls = [];

    for (const file of files) {
      const url = await uploadFile(file); // uploadFile is your storage helper
      urls.push(url);
    }
    const updatedUrls = [...existingUrls, ...urls];
    await updateGalleryHeroConfig({ backgroundUrls: updatedUrls });
    return updatedUrls;
  };

  // Remove image at a given index
  const deleteGalleryHeroImage = async (indexToRemove) => {
    const heroRef = ref(db, "gallery/hero");
    const snapshot = await get(heroRef);
    const data = snapshot.val() || {};
    const urls = data.backgroundUrls || [];
    const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);
    await updateGalleryHeroConfig({ backgroundUrls: updatedUrls });
    return updatedUrls;
  };

  // ---------------- GALLERY - Photos ----------------

  const [galleryData, setGalleryData] = useState([]);

  // Fetch gallery data and sync real-time. Returns the unsubscribe function
  // so callers (useEffect) can clean up when the active property changes.
  const fetchGalleryData = () => {
    const galleryRef = ref(db, homePath("gallery/photos"));
    return onValue(galleryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((sectionId) => ({
          id: sectionId,
          name: data[sectionId].name,
          icon: data[sectionId].icon || null,
          images: data[sectionId].images
            ? Object.keys(data[sectionId].images).map((imageId) => ({
                id: imageId,
                ...data[sectionId].images[imageId],
              }))
            : [],
        }));
        setGalleryData(formattedData);
      } else {
        setGalleryData([]);
      }
    });
  };

  // Add new gallery section
  const addGallerySection = async (sectionName, icon = null) => {
    const sectionRef = ref(db, homePath("gallery/photos"));
    const newSectionRef = push(sectionRef);
    const sectionData = {
      name: sectionName,
      icon: icon || null,
      images: {}, // initialize as empty object
    };
    await set(newSectionRef, sectionData);
  };

  // Update the icon for a gallery section
  const updateGallerySectionIcon = async (sectionId, icon) => {
    const sectionRef = ref(db, homePath(`gallery/photos/${sectionId}`));
    await update(sectionRef, { icon: icon || null });
  };

  // Upload file and add image with label to a section
  const addImageToSection = async (sectionId, file, label) => {
    const downloadURL = await uploadFile(file);
    const imagesRef = ref(db, homePath(`gallery/photos/${sectionId}/images`));
    const newImageRef = push(imagesRef);
    const imageData = {
      url: downloadURL,
      label: label,
    };
    await set(newImageRef, imageData);
  };

  // Delete an image from a section
  const deleteImageFromSection = async (sectionId, imageId) => {
    const imageRef = ref(db, homePath(`gallery/photos/${sectionId}/images/${imageId}`));
    await remove(imageRef);
  };

  // Delete an entire gallery section
  const deleteGallerySection = async (sectionId) => {
    const sectionRef = ref(db, homePath(`gallery/photos/${sectionId}`));
    await remove(sectionRef);
  };

  // Rename/update gallery section name
  const updateGallerySectionName = async (sectionId, newName) => {
    const sectionRef = ref(db, homePath(`gallery/photos/${sectionId}`));
    await update(sectionRef, { name: newName });
  };

  // Fetch gallery data on mount and whenever the active property changes.
  useEffect(() => {
    const unsubscribe = fetchGalleryData();
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [activeHomeProperty]);

  const updateImageLabel = async (sectionId, imageId, newLabel) => {
    const imageRef = ref(db, homePath(`gallery/photos/${sectionId}/images/${imageId}`));
    await update(imageRef, { label: newLabel });
  };

  // Define the base path for beauty gallery
  const [beautyGalleryData, setBeautyGalleryData] = useState([]);
  const BEAUTY_GALLERY_BASE_PATH = "gallery/beauty";

  // Fetch beauty gallery data and sync real-time
  const fetchBeautyGalleryData = () => {
    const galleryRef = ref(db, BEAUTY_GALLERY_BASE_PATH);
    onValue(galleryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((sectionId) => ({
          id: sectionId,
          name: data[sectionId].name,
          images: data[sectionId].images
            ? Object.keys(data[sectionId].images).map((imageId) => ({
                id: imageId,
                ...data[sectionId].images[imageId],
              }))
            : [],
        }));
        setBeautyGalleryData(formattedData); // ✅ use correct setter
      } else {
        setBeautyGalleryData([]); // ✅ reset to empty if no data
      }
    });
  };

  // Add new beauty gallery section
  const addBeautyGallerySection = async (sectionName) => {
    const sectionRef = ref(db, BEAUTY_GALLERY_BASE_PATH);
    const newSectionRef = push(sectionRef);
    const sectionData = {
      name: sectionName,
      images: {}, // initialize empty images
    };
    await set(newSectionRef, sectionData);
  };

  // Upload image to a beauty section
  const addImageToBeautySection = async (sectionId, file, label) => {
    const downloadURL = await uploadFile(file);
    const imagesRef = ref(
      db,
      `${BEAUTY_GALLERY_BASE_PATH}/${sectionId}/images`
    );
    const newImageRef = push(imagesRef);
    const imageData = {
      url: downloadURL,
      label: label,
    };
    await set(newImageRef, imageData);
  };

  // Delete an image from beauty section
  const deleteImageFromBeautySection = async (sectionId, imageId) => {
    const imageRef = ref(
      db,
      `${BEAUTY_GALLERY_BASE_PATH}/${sectionId}/images/${imageId}`
    );
    await remove(imageRef);
  };

  // Delete a beauty gallery section
  const deleteBeautyGallerySection = async (sectionId) => {
    const sectionRef = ref(db, `${BEAUTY_GALLERY_BASE_PATH}/${sectionId}`);
    await remove(sectionRef);
  };

  // Update beauty gallery section name
  const updateBeautyGallerySectionName = async (sectionId, newName) => {
    const sectionRef = ref(db, `${BEAUTY_GALLERY_BASE_PATH}/${sectionId}`);
    await update(sectionRef, { name: newName });
  };

  // Update label for a beauty gallery image
  const updateBeautyImageLabel = async (sectionId, imageId, newLabel) => {
    const imageRef = ref(
      db,
      `${BEAUTY_GALLERY_BASE_PATH}/${sectionId}/images/${imageId}`
    );
    await update(imageRef, { label: newLabel });
  };

  // -----------------  TESTIMONIALS -----------------

  const addReview = async (reviewData, imageFile) => {
    const reviewsRef = ref(db, "testimonials/reviews");
    const newReviewRef = push(reviewsRef);

    let imageUrl = "";
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the image and get the URL
    }

    const newReview = {
      ...reviewData,
      imageUrl,
      createdAt: new Date().toISOString(),
    };

    await set(newReviewRef, newReview); // Save the review in the database
  };

  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const reviewsRef = ref(db, "testimonials/reviews");
    return onValue(reviewsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setReviews(formattedData);
      } else {
        setReviews([]);
      }
    });
  }, []);

  const updateReview = async (id, updates, imageFile) => {
    const reviewRef = ref(db, `testimonials/reviews/${id}`);

    let imageUrl = updates.imageUrl; // Use the existing image URL by default
    if (imageFile) {
      imageUrl = await uploadFile(imageFile); // Upload the file and get the new URL
    }

    const updatedReview = {
      ...updates,
      imageUrl, // Update the image URL
    };

    await update(reviewRef, updatedReview); // Update the review data in Firebase
  };


// ----------Faqs---------

// Fetch FAQ hero config (array of images)
// --- FAQ Hero Config State ---
const [faqHeroConfig, setFaqHeroConfig] = useState({
  heroText: "",
  highlightText: "",
  backgroundUrls: [],
});

// --- Fetch (on mount) ---
useEffect(() => {
  const faqHeroRef = ref(db, "faq/hero");
  return onValue(faqHeroRef, (snap) => {
    const data = snap.val() || {};
    setFaqHeroConfig({
      heroText: data.heroText || "",
      highlightText: data.highlightText || "",
      backgroundUrls: Array.isArray(data.backgroundUrls)
        ? data.backgroundUrls
        : data.backgroundUrl
        ? [data.backgroundUrl] // fallback if single string
        : [],
    });
  });
}, []);

// --- Update FAQ hero text or other fields (partial update) ---
const updateFaqHeroConfig = async (updates) => {
  const faqHeroRef = ref(db, "faq/hero");
  await update(faqHeroRef, updates);
};

// --- Upload new images and append them ---
const uploadFaqHeroBackground = async (files) => {
  const urls = [];
  for (const file of files) {
    const url = await uploadFile(file); // Your own upload utility
    urls.push(url);
  }

  // Fetch current backgroundUrls array from DB (ensure atomicity for multiple admins)
  const faqHeroRef = ref(db, "faq/hero");
  const snapshot = await get(faqHeroRef);
  const existingData = snapshot.val() || {};
  const existingUrls = Array.isArray(existingData.backgroundUrls)
    ? existingData.backgroundUrls
    : [];

  const updatedUrls = [...existingUrls, ...urls];

  // Save new array to DB
  await updateFaqHeroConfig({ backgroundUrls: updatedUrls });

  return urls; // Return just the new URLs for local preview if needed
};

// --- Delete one image by index (from array) ---
const deleteFaqHeroBackgroundImage = async (indexToRemove) => {
  const faqHeroRef = ref(db, "faq/hero");
  const snapshot = await get(faqHeroRef);
  const data = snapshot.val() || {};
  const urls = Array.isArray(data.backgroundUrls)
    ? data.backgroundUrls
    : [];
  const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);
  await updateFaqHeroConfig({ backgroundUrls: updatedUrls });
};



  // -------- Contact Us --------

  // State for contact hero data

const [contactHeroData, setContactHeroData] = useState({
  title: "",
  highlightText: "",
  backgroundUrls: [], // always array for consistency
});

useEffect(() => {
  const contactHeroRef = ref(db, "contact/hero");
  return onValue(contactHeroRef, (snapshot) => {
    const data = snapshot.val() || {};
    setContactHeroData({
      title: data.title || "",
      highlightText: data.highlightText || "",
      backgroundUrls: Array.isArray(data.backgroundUrls)
        ? data.backgroundUrls
        : data.backgroundUrl
          ? [data.backgroundUrl] // migrate old data
          : [],
    });
  });
}, []);

// Update (title/highlightText and append images)
const updateContactHeroData = async (updates, files = []) => {
  const contactHeroRef = ref(db, "contact/hero");
  const snap = await get(contactHeroRef);
  const data = snap.val() || {};
  const existingUrls = Array.isArray(data.backgroundUrls)
    ? data.backgroundUrls
    : data.backgroundUrl
      ? [data.backgroundUrl]
      : [];

  let newUrls = [];
  if (files && files.length > 0) {
    for (const file of files) {
      const url = await uploadFile(file);
      newUrls.push(url);
    }
  }
  const updatedUrls = [...existingUrls, ...newUrls];

  await set(contactHeroRef, {
    ...data,
    ...updates,
    backgroundUrls: updatedUrls,
  });
  return updatedUrls;
};

// Delete by index
const deleteContactHeroImage = async (indexToRemove) => {
  const contactHeroRef = ref(db, "contact/hero");
  const snap = await get(contactHeroRef);
  const data = snap.val() || {};
  const urls = Array.isArray(data.backgroundUrls)
    ? data.backgroundUrls
    : data.backgroundUrl
      ? [data.backgroundUrl]
      : [];
  const updatedUrls = urls.filter((_, idx) => idx !== indexToRemove);

  await set(contactHeroRef, {
    ...data,
    backgroundUrls: updatedUrls,
  });
  return updatedUrls;
};


  // State for contact section content
  const [contactSectionContent, setContactSectionContent] = useState({
    title: "Have Any Question?",
    address:
      "Subathu Road, Dharampur, Teh. Kasauli, Himachal Pradesh, Pincode - 173209",
    phone: "+91 8644864490",
    email: "contact@example.com",
  });

  // Fetch contact section content
  useEffect(() => {
    const contactContentRef = ref(db, "contact/content");
    return onValue(contactContentRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setContactSectionContent(data);
      }
    });
  }, []);

  // Update contact section content
  const updateContactSectionContent = async (contentData) => {
    const contactContentRef = ref(db, "contact/content");
    await set(contactContentRef, contentData); // Save the updated content to Firebase
  };

  // State for contact form submissions
  const [contactFormSubmissions, setContactFormSubmissions] = useState([]);

  // Fetch contact form submissions
  useEffect(() => {
    const contactFormRef = ref(db, "contact/form");
    return onValue(contactFormRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = Object.keys(data).map((id) => ({
          id,
          ...data[id],
        }));
        setContactFormSubmissions(formattedData);
      } else {
        setContactFormSubmissions([]);
      }
    });
  }, []);

  const deleteContactFormSubmission = async (id) => {
    await remove(ref(db, `contact/form/${id}`));
  };

  return (
    <DataContext.Provider
      value={{
        // Active property for Home tabs (admin)
        activeHomeProperty,
        setActiveHomeProperty,
        cloneVivaakDataTo,

        // Header1
        headerLinks,
        addHeaderLink,
        updateHeaderLink,
        deleteHeaderLink,
        headerConfig,
        uploadHeaderAsset,

        // Header2
        header2Links,
        addHeader2Link,
        updateHeader2Link,
        deleteHeader2Link,
        header2Config,
        uploadHeader2Asset,

        // Home
        heroConfig,
        updateHeroConfig,
        uploadHeroBackground,
        deleteHeroBackgroundImage,
        aboutConfig,
        updateAboutConfig,
        uploadAboutImage,

        vivaakOfferings,
        postVivaakOfferings,

        amenitiesServices,

        // Gallery
        galleryData,
        addGallerySection,
        addImageToSection,
        deleteImageFromSection,
        deleteGallerySection,
        updateGallerySectionName,
        updateGallerySectionIcon,
        updateImageLabel,

        // Carousel
        carouselData,
        addCarouselCard,
        updateCarouselCard,
        deleteCarouselCard,

        // Villa in Motion Gallery
        villaVideos,
        addVillaVideo,
        updateVillaVideo,
        deleteVillaVideo,

        // Local Attractions
        localAttractions,
        addLocalAttraction,
        updateLocalAttraction,
        deleteLocalAttraction,

        // Special Offers
        specialOffers,
        addSpecialOffer,
        updateSpecialOffer,
        deleteSpecialOffer,

        // Testimonials
        testimonials,
        addTestimonial,
        updateTestimonial,
        deleteTestimonial,

        // FAQs
        faqs,
        addFaq,
        updateFaq,
        deleteFaq,

        // We Make It Special
        weMakeItSpecialContent,
        updateWeMakeItSpecialContent,

        // Blogs
        blogs,
        addBlog,
        updateBlog,
        deleteBlog,

        // Flavours
        flavoursHeroConfig,
        updateFlavoursHeroConfig,
        uploadFlavoursHeroBackground,
        deleteFlavoursHeroImage,

        // Our Chefs
        ourChefs,
        addChef,
        updateChef,
        deleteChef,

        // Meal Types
        mealTypes,
        addMealType,
        updateMealType,
        deleteMealType,

        // Explore Your Menu
        exploreMenuData,
        updateExploreMenu,
        deleteExploreMenu,

        // Hospitality Team
        hospitalityTeamData,
        updateHospitalityTeam,
        deleteHospitalityTeam,

        // Team Photos
        teamPhotos,
        addTeamPhoto,
        deleteTeamPhoto,

        // About Hero
        aboutHeroConfig,
        postAboutHeroConfig,
        updateAboutHeroConfig,
        deleteAboutHeroBackgroundImage,

        // About Info
        aboutInfoData,
        updateAboutInfo,
        deleteAboutInfo,

        // Family Legacy
        familyLegacyData,
        updateFamilyLegacy,
        deleteFamilyLegacy,

        // Facilities
        facilitiesData,
        addFacility,
        updateFacility,
        deleteFacility,

        // Amenities & Services
        amenitiesData,
        addAmenity,
        updateAmenity,
        deleteAmenity,

        aboutVideo,
        updateAboutVideo,

        // Gallery Hero
        galleryHeroConfig,
        updateGalleryHeroConfig,
        uploadGalleryHeroBackground,
        deleteGalleryHeroImage,

        // Gallery beauti
        beautyGalleryData,
        fetchBeautyGalleryData,
        addBeautyGallerySection,
        addImageToBeautySection,
        deleteImageFromBeautySection,
        deleteBeautyGallerySection,
        updateBeautyGallerySectionName,
        updateBeautyImageLabel,

        faqHeroConfig,
        updateFaqHeroConfig,
        uploadFaqHeroBackground,
        deleteFaqHeroBackgroundImage,

        // Testimonials
        reviews,
        addReview,
        updateReview,

        // Contact Us
        contactHeroData,
        updateContactHeroData,
        deleteContactHeroImage,
        contactSectionContent,
        updateContactSectionContent,
        contactFormSubmissions,
        deleteContactFormSubmission,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
